/**
 * Validation Rules Configuration
 * Defines validation rules for questionnaire responses
 */

// Validation rule types
export const VALIDATION_TYPES = {
  REQUIRED: 'required',
  EMAIL: 'email',
  URL: 'url',
  NUMERIC: 'numeric',
  NUMERIC_RANGE: 'numeric_range',
  DATE: 'date',
  DATE_RANGE: 'date_range',
  MIN_LENGTH: 'min_length',
  MAX_LENGTH: 'max_length',
  REGEX: 'regex',
  CUSTOM: 'custom'
};

// Default validation rules
export const DEFAULT_VALIDATION_RULES = {
  // Required field validation
  required: {
    type: VALIDATION_TYPES.REQUIRED,
    validate: (value) => {
      return value && value.trim().length > 0;
    },
    message: 'This field is required'
  },

  // Email validation
  email: {
    type: VALIDATION_TYPES.EMAIL,
    validate: (value) => {
      if (!value) return true; // Optional unless combined with required
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    },
    message: 'Please enter a valid email address'
  },

  // URL validation
  url: {
    type: VALIDATION_TYPES.URL,
    validate: (value) => {
      if (!value) return true;
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    message: 'Please enter a valid URL (e.g., https://example.com)'
  },

  // Numeric validation
  numeric: {
    type: VALIDATION_TYPES.NUMERIC,
    validate: (value) => {
      if (!value) return true;
      return !isNaN(parseFloat(value)) && isFinite(value);
    },
    message: 'Please enter a valid number'
  },

  // Date validation
  date: {
    type: VALIDATION_TYPES.DATE,
    validate: (value) => {
      if (!value) return true;
      const date = new Date(value);
      return date instanceof Date && !isNaN(date);
    },
    message: 'Please enter a valid date'
  }
};

/**
 * Create a numeric range validator
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {object} Validation rule
 */
export const numericRange = (min, max) => ({
  type: VALIDATION_TYPES.NUMERIC_RANGE,
  validate: (value) => {
    if (!value) return true;
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    return num >= min && num <= max;
  },
  message: `Value must be between ${min} and ${max}`
});

/**
 * Create a min length validator
 * @param {number} minLength - Minimum length
 * @returns {object} Validation rule
 */
export const minLength = (minLength) => ({
  type: VALIDATION_TYPES.MIN_LENGTH,
  validate: (value) => {
    if (!value) return true;
    return value.trim().length >= minLength;
  },
  message: `Minimum ${minLength} characters required`
});

/**
 * Create a max length validator
 * @param {number} maxLength - Maximum length
 * @returns {object} Validation rule
 */
export const maxLength = (maxLength) => ({
  type: VALIDATION_TYPES.MAX_LENGTH,
  validate: (value) => {
    if (!value) return true;
    return value.trim().length <= maxLength;
  },
  message: `Maximum ${maxLength} characters allowed`
});

/**
 * Create a custom regex validator
 * @param {RegExp} regex - Regular expression
 * @param {string} message - Error message
 * @returns {object} Validation rule
 */
export const regexPattern = (regex, message) => ({
  type: VALIDATION_TYPES.REGEX,
  validate: (value) => {
    if (!value) return true;
    return regex.test(value);
  },
  message: message || 'Invalid format'
});

/**
 * Create a custom validator
 * @param {function} validateFn - Custom validation function
 * @param {string} message - Error message
 * @returns {object} Validation rule
 */
export const customValidator = (validateFn, message) => ({
  type: VALIDATION_TYPES.CUSTOM,
  validate: validateFn,
  message: message || 'Validation failed'
});

/**
 * Question-specific validation rules
 * Maps question IDs to their validation rules
 */
export const QUESTION_VALIDATION_RULES = {
  // Example: Email questions
  'contact_email': [DEFAULT_VALIDATION_RULES.required, DEFAULT_VALIDATION_RULES.email],
  
  // Example: Numeric questions with ranges
  'fund_size': [DEFAULT_VALIDATION_RULES.required, DEFAULT_VALIDATION_RULES.numeric, numericRange(0, 1000000000)],
  
  // Example: URL questions
  'company_website': [DEFAULT_VALIDATION_RULES.url],
  
  // Default: All required questions
  default: []
};

